import React, { Component, ChangeEvent } from 'react';
import { Formik } from 'formik';
import { connect } from 'react-redux';
import classnames from 'classnames';
import * as Yup from 'yup';
import { PageView } from '@jg/react-tracking';
import { Helmet } from 'react-helmet';
import { Trans, withTranslation } from 'react-i18next';
import {
  getRequestError,
  getRequestInFlight,
} from '../../selectors/httpSelectors';
import { IAppState } from '../../types/state.d';
import {
  apiThunk as apiThunkAction,
  httpActions,
  appActions,
  appThunk as appThunkAction,
  apiActions,
} from '../../actions';
import {
  REQUEST,
  SHORT_NAME_MINIMUM_CHARACTER,
  SHORT_NAME_MAXIMUM_CHARACTER,
  TARGET_MIN,
  TARGET_MAX,
  ROUTES,
} from '../../consts';
import {
  TFundraisingSettings,
  IFundraisingSettingsDispatchToProps,
  IFundraisingSettingsMapStateToProps,
  IFundraisingSettingsFormProps,
} from './FundraisingSettings.d';
import FundraisingSettingsForm from '../../components/FundraisingSettingsForm';
import {
  isShortNameValid,
  getCurrencyCode,
  getCharity,
  getCharityGDPRConsent,
  getIsGiftAidable,
  getTeamMembersTargetAmount,
} from '../../selectors/apiSelectors';
import FatalError from '../../components/FatalError';
import { getNewFrpData } from '../../selectors/appSelectors';
import JoinStepNavigationContainer from '../JoinStepNaviagtionContainer/JoinStepNaviagtionContainer';

export class FundraisingSettings extends Component<TFundraisingSettings> {
  setTimeoutId = 0;

  componentDidMount() {
    const { fetchGDPRConsents } = this.props;
    fetchGDPRConsents();
  }

  componentWillUnmount() {
    clearTimeout(this.setTimeoutId);
  }

  handleOnKeyUp = (event: Pick<ChangeEvent<any>, 'persist' | 'target'>) => {
    const { checkFrpShortName } = this.props;
    event.persist();
    window.clearTimeout(this.setTimeoutId);
    this.setTimeoutId = window.setTimeout(() => {
      checkFrpShortName(event.target.value);
    }, 300);
  };

  handleSubmit = (data: IFundraisingSettingsFormProps) => {
    const {
      createFundraisingPage,
      saveNewFrpData,
      pushToPage,
      isIsGiftAidable,
      isFrpShortNameValid,
    } = this.props;
    if (isFrpShortNameValid !== true) {
      return;
    }
    if (isIsGiftAidable) {
      saveNewFrpData(data);
      pushToPage(ROUTES.GIFT_AID);
    } else {
      const {
        shortName: pageShortName,
        fundraisingTarget: targetAmount,
        gdprOptIn,
      } = data;
      createFundraisingPage({
        pageShortName,
        targetAmount,
        gdprOptIn,
      });
    }
  };

  render() {
    const {
      t,
      isError,
      isFrpShortNameValid,
      currencyCode,
      resetRequestError,
      frpData: { shortName, gdprOptIn },
      isInFlight,
      charity,
      charityGDPRConsent,
      isIsGiftAidable,
      fundraisingTarget,
      shortNameCheckIsInFlight,
      shortNameCheckIsError,
      clearShortNameValidity,
    } = this.props;
    return (
      <PageView
        trackingData={{
          subtype: 'join team',
          event_value: 'fundraising page creation',
          page_section:
            'join team > fundraising page creation > fundraising settings',
        }}
      >
        <Helmet title={t('title.join.createFRP')} />
        <main data-qa="fundraising-settings-page">
          <JoinStepNavigationContainer />
          <h1
            className={classnames('container--heading jg-space-mbxs', {
              'jg-space-ptlg': !isIsGiftAidable,
            })}
          >
            <Trans i18nKey="content.fundraisingsettings.heading" />
          </h1>
          <p className="jg-text--large container--heading jg-space-mbmd">
            <Trans i18nKey="content.fundraisingsettings.headingsubtext" />
          </p>
          <Formik<IFundraisingSettingsFormProps>
            initialValues={{
              shortName,
              fundraisingTarget,
              gdprOptIn,
            }}
            validationSchema={Yup.object().shape({
              shortName: Yup.string()
                .required(
                  t(
                    'content.fundraisingsettings.shortname.fundraisinglinkrequired',
                  ),
                )
                .min(
                  SHORT_NAME_MINIMUM_CHARACTER,
                  t(
                    'content.fundraisingsettings.shortname.fundraisinglinkmincharacter',
                  ),
                )
                .max(
                  SHORT_NAME_MAXIMUM_CHARACTER,
                  t(
                    'content.fundraisingsettings.shortname.fundraisinglinkmaxcharacter',
                  ),
                )
                .matches(
                  /^[a-z0-9]{1}[a-z0-9-]+$/i,
                  t(
                    'content.fundraisingsettings.shortname.fundraisinglinknotallowed',
                  ),
                ),
              fundraisingTarget: Yup.number()
                .min(
                  TARGET_MIN,
                  t('content.fundraisingsettings.fundraisingtargetmin'),
                )
                .max(
                  TARGET_MAX,
                  t('content.fundraisingsettings.fundraisingtargetmax'),
                ),
            })}
            onSubmit={this.handleSubmit}
            render={
              /* istanbul ignore next */ formikProps => (
                <FundraisingSettingsForm
                  {...formikProps}
                  handleOnKeyUp={this.handleOnKeyUp}
                  isFrpShortNameValid={isFrpShortNameValid}
                  currencyCode={currencyCode}
                  isInFlight={isInFlight}
                  shortNameCheckIsInFlight={shortNameCheckIsInFlight}
                  charity={charity}
                  charityGDPRConsent={charityGDPRConsent}
                  clearShortNameValidity={clearShortNameValidity}
                  shortNameCheckIsError={shortNameCheckIsError}
                />
              )
            }
          />
          {(isError || shortNameCheckIsError) && (
            <FatalError closeButtonClick={resetRequestError} />
          )}
        </main>
      </PageView>
    );
  }
}

export const mapDispatchToProps: IFundraisingSettingsDispatchToProps = {
  checkFrpShortName: apiThunkAction.checkFrpShortName,
  createFundraisingPage: apiThunkAction.createFundraisingPage,
  resetRequestError: httpActions.resetRequestError,
  saveNewFrpData: appActions.saveNewFrpData,
  pushToPage: appThunkAction.pushToPage,
  fetchGDPRConsents: apiThunkAction.fetchGDPRConsents,
  clearShortNameValidity: apiActions.clearShortNameValidity,
};

export const mapStateToProps = (
  state: IAppState,
): IFundraisingSettingsMapStateToProps => ({
  isInFlight: getRequestInFlight(REQUEST.CREATE_FUNDRAISING_PAGE)(state),
  isError: getRequestError(REQUEST.CREATE_FUNDRAISING_PAGE)(state),
  shortNameCheckIsInFlight: getRequestInFlight(REQUEST.CHECK_FRP_SHORT_NAME)(
    state,
  ),
  shortNameCheckIsError: getRequestError(REQUEST.CHECK_FRP_SHORT_NAME)(state),
  isFrpShortNameValid: isShortNameValid(state),
  currencyCode: getCurrencyCode(state),
  charity: getCharity(state),
  charityGDPRConsent: getCharityGDPRConsent(state),
  isIsGiftAidable: getIsGiftAidable(state),
  frpData: getNewFrpData(state),
  fundraisingTarget: getTeamMembersTargetAmount(state),
});

export default withTranslation()(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(FundraisingSettings),
);

// TODO convert to single thunk and move logic and do in thunk??
// TODO Move shortname functionality inside form?
